import { useEffect, useState } from 'react';
import { adminClient } from '../../api/client';
import { Link } from 'react-router-dom';

interface Loan {
  loanId: string; ISBN: string; bookTitle: string; bookAuthor: string;
  userId: string; userEmail: string; userName: string;
  checkoutDate: string; returnDueDate: string; isOverdue: boolean; loanSK: string;
}

interface BookLoan {
  loanId: string; userId: string; userEmail: string; userName: string;
  checkoutDate: string; returnDueDate: string; returnedDate?: string;
  status: 'active' | 'returned'; renewalCount: number; isOverdue: boolean;
}

interface BookLoanModal {
  isbn: string;
  title: string;
}

export function AdminDashboardPage() {
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookModal, setBookModal] = useState<BookLoanModal | null>(null);
  const [bookLoans, setBookLoans] = useState<BookLoan[]>([]);
  const [bookLoansLoading, setBookLoansLoading] = useState(false);

  useEffect(() => {
    adminClient.get('/loans/active')
      .then(res => setLoans(res.data.data.loans))
      .finally(() => setLoading(false));
  }, []);

  function openBookLoans(isbn: string, title: string) {
    setBookModal({ isbn, title });
    setBookLoans([]);
    setBookLoansLoading(true);
    adminClient.get(`/loans/book/${isbn}`)
      .then(res => setBookLoans(res.data.data.loans))
      .finally(() => setBookLoansLoading(false));
  }

  const overdue = loans.filter(l => l.isOverdue);
  const active = loans.filter(l => !l.isOverdue);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-serif font-bold text-stone-900">Admin Dashboard</h1>
        <div className="flex gap-3">
          <Link to="/admin/alerts" className="text-sm bg-amber-50 text-amber-700 border border-amber-200 px-4 py-2 rounded-lg hover:bg-amber-100 transition-colors">AI Alerts</Link>
          <Link to="/admin/insights" className="text-sm bg-brand-50 text-brand-700 border border-brand-200 px-4 py-2 rounded-lg hover:bg-brand-100 transition-colors">Insights</Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { label: 'Active Loans', value: loans.length, color: 'brand' },
          { label: 'Overdue', value: overdue.length, color: 'red' },
          { label: 'On Time', value: active.length, color: 'green' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl border border-stone-100 shadow-sm p-5">
            <p className="text-sm text-stone-500 mb-1">{label}</p>
            <p className={`text-3xl font-bold text-${color}-600`}>{value}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-100 flex items-center justify-between">
            <h2 className="font-semibold text-stone-800">All Active Loans</h2>
            <p className="text-xs text-stone-400">Click a book title to see full loan history</p>
          </div>
          {loans.length === 0 ? (
            <p className="text-center text-stone-400 py-10">No active loans.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs text-stone-500 uppercase tracking-wide">
                <tr>
                  {['Book', 'Borrower', 'Checkout', 'Due', 'Status'].map(h => (
                    <th key={h} className="px-5 py-3 text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {loans.map(loan => (
                  <tr key={loan.loanId} className="hover:bg-stone-50 transition-colors">
                    <td className="px-5 py-3">
                      <button
                        onClick={() => openBookLoans(loan.ISBN, loan.bookTitle)}
                        className="font-medium text-brand-700 hover:text-brand-900 hover:underline text-left"
                      >
                        {loan.bookTitle}
                      </button>
                      <p className="text-xs text-stone-400">{loan.bookAuthor}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-stone-700">{loan.userName}</p>
                      <p className="text-xs text-stone-400">{loan.userEmail}</p>
                    </td>
                    <td className="px-5 py-3 text-stone-500">{new Date(loan.checkoutDate).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-stone-500">{new Date(loan.returnDueDate).toLocaleDateString()}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${loan.isOverdue ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {loan.isOverdue ? 'Overdue' : 'On time'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Book loan history modal */}
      {bookModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setBookModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-stone-100 flex items-start justify-between">
              <div>
                <h2 className="font-serif font-bold text-stone-900 text-lg">{bookModal.title}</h2>
                <p className="text-xs text-stone-400 mt-0.5">Full loan history</p>
              </div>
              <button onClick={() => setBookModal(null)} className="text-stone-400 hover:text-stone-600 text-xl leading-none mt-0.5">×</button>
            </div>

            <div className="overflow-y-auto flex-1">
              {bookLoansLoading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
                </div>
              ) : bookLoans.length === 0 ? (
                <p className="text-center text-stone-400 py-10 text-sm">No loan history for this book.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-xs text-stone-500 uppercase tracking-wide sticky top-0">
                    <tr>
                      {['Borrower', 'Checkout', 'Due / Returned', 'Status'].map(h => (
                        <th key={h} className="px-5 py-3 text-left font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-50">
                    {bookLoans.map(loan => (
                      <tr key={loan.loanId} className="hover:bg-stone-50">
                        <td className="px-5 py-3">
                          <p className="font-medium text-stone-800">{loan.userName}</p>
                          <p className="text-xs text-stone-400">{loan.userEmail}</p>
                        </td>
                        <td className="px-5 py-3 text-stone-500">
                          {new Date(loan.checkoutDate).toLocaleDateString()}
                        </td>
                        <td className="px-5 py-3 text-stone-500">
                          {loan.returnedDate
                            ? new Date(loan.returnedDate).toLocaleDateString()
                            : new Date(loan.returnDueDate).toLocaleDateString()}
                        </td>
                        <td className="px-5 py-3">
                          {loan.status === 'returned' ? (
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-stone-100 text-stone-600">Returned</span>
                          ) : loan.isOverdue ? (
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700">Overdue</span>
                          ) : (
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">On loan</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="px-6 py-3 border-t border-stone-100 text-xs text-stone-400 flex gap-4">
              <span>{bookLoans.filter(l => l.status === 'active').length} currently on loan</span>
              <span>{bookLoans.filter(l => l.status === 'returned').length} returned</span>
              <span>{bookLoans.length} total loans</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
